import { Space, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { AbrangenciaTuple, OutdatedDivergenceRow, OutdatedDivergenceType } from '../../utils/abrangenciaCompare';

const SOLUCIONARE_SEARCHES = new Set(['tjes', 'tjpi', 'tjrn', 'tjto']);

const isSolucionareSearch = (search: string | undefined | null): boolean => {
  if (!search) return false;
  return SOLUCIONARE_SEARCHES.has(String(search).trim().toLowerCase());
};

const renderSearchCell = (search: string): React.ReactNode => (
  <Space size={8} wrap>
    <span>{search}</span>
    {isSolucionareSearch(search) ? <Tag color="geekblue">Solucionare</Tag> : null}
  </Space>
);

const ABRANGENCIA_FIELDS: (keyof AbrangenciaTuple)[] = ['search', 'platform', 'query', 'key'];

const COLUMN_TITLES: Record<keyof AbrangenciaTuple, string> = {
  search: 'Search',
  platform: 'Platform',
  query: 'Query',
  key: 'Key',
};

const buildStringSorter = (field: keyof AbrangenciaTuple) =>
  (a: AbrangenciaTuple, b: AbrangenciaTuple): number =>
    String(a[field] ?? '').localeCompare(String(b[field] ?? ''), 'pt-BR');

const buildColumnFilters = (data: AbrangenciaTuple[], field: keyof AbrangenciaTuple) => {
  const values = Array.from(new Set(data.map((row) => String(row[field] ?? '')))).sort((a, b) =>
    a.localeCompare(b, 'pt-BR'),
  );

  return values.map((value) => ({ text: value, value }));
};

export const abrangenciaColumns: ColumnsType<AbrangenciaTuple> = [
  {
    title: 'Search',
    dataIndex: 'search',
    key: 'search',
    ellipsis: true,
    render: (search: string) => renderSearchCell(search),
  },
  { title: 'Platform', dataIndex: 'platform', key: 'platform', ellipsis: true },
  { title: 'Query', dataIndex: 'query', key: 'query', ellipsis: true },
  { title: 'Key', dataIndex: 'key', key: 'key', ellipsis: true },
];

/** Colunas com ordenação e filtro por valor (modais de sincronização) */
export const buildModalAbrangenciaColumns = (data: AbrangenciaTuple[]): ColumnsType<AbrangenciaTuple> =>
  ABRANGENCIA_FIELDS.map((field) => ({
    title: COLUMN_TITLES[field],
    dataIndex: field,
    key: field,
    ellipsis: true,
    sorter: buildStringSorter(field),
    filters: buildColumnFilters(data, field),
    onFilter: (value, record) => String(record[field] ?? '') === String(value),
    filterSearch: true,
  }));

const DIVERGENCE_LABELS: Record<OutdatedDivergenceType, string> = {
  missingInStatus: 'Ausente no status',
  extraInStatus: 'Extra no status',
};

/** Colunas do alerta de divergência produção × status, com tipo de divergência */
export const buildOutdatedDivergenceColumns = (
  data: OutdatedDivergenceRow[],
): ColumnsType<OutdatedDivergenceRow> => {
  const abrangenciaColumns = buildModalAbrangenciaColumns(data) as ColumnsType<OutdatedDivergenceRow>;

  return [
    {
      title: 'Divergência',
      dataIndex: 'divergenceType',
      key: 'divergenceType',
      ellipsis: true,
      sorter: (a, b) =>
        DIVERGENCE_LABELS[a.divergenceType].localeCompare(DIVERGENCE_LABELS[b.divergenceType], 'pt-BR'),
      filters: (['missingInStatus', 'extraInStatus'] as OutdatedDivergenceType[]).map((type) => ({
        text: DIVERGENCE_LABELS[type],
        value: type,
      })),
      onFilter: (value, record) => record.divergenceType === value,
      render: (type: OutdatedDivergenceType) => DIVERGENCE_LABELS[type],
    },
    ...abrangenciaColumns,
  ];
};
